import React, { useEffect, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useParams } from 'react-router';
import { Box } from '@mui/material';
import { useGetTunnelActionsQuery, useGetTunnelsQuery } from 'api/main';
import { texts } from 'utils/languages/fr';
import { CancelButton, Loader } from 'components';
import { processTunnels } from 'utils/helpers/tunnels';
import ActiveTunnelTabs from './active-version/ActiveTunnelTabs';
const rootSx = {
    padding: theme => theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme => theme.spacing(1),
};
const buttonContainerSx = {
    textAlign: 'center',
    mt: theme => theme.spacing(2),
};
const TunnelActiveVersion = ({ onCancel }) => {
    const { fundId } = useParams();
    const [tunnelActionResults, setTunnelActionResults] = useState([]);

    const { data: fundTunnels = [], isLoading: tunnelLoader } = useGetTunnelsQuery({
        investmentFunds: fundId,
    });

    const tunnelsByStatus = useMemo(
        () => ({
            active: fundTunnels.filter(tunnel => tunnel.status === 'active'),
            defaultActive: fundTunnels.filter(tunnel => tunnel.status === 'default_active'),
        }),
        [fundTunnels]
    );

    const allTunnelIds = [...tunnelsByStatus.active, ...tunnelsByStatus.defaultActive].map(
        tunnel => tunnel.id
    );

    const tunnelActionsQueries = allTunnelIds.map(tunnelId =>
        // eslint-disable-next-line
        useGetTunnelActionsQuery({ tunnel: tunnelId }, { skip: !tunnelId })
    );

    const tunnelActionsLoading = tunnelActionsQueries.some(query => query.isLoading);

    useEffect(() => {
        tunnelActionsQueries.forEach((query, index) => {
            if (query.data) {
                setTunnelActionResults(prev => {
                    const exists = prev.some(r => r.tunnelId === allTunnelIds[index]);
                    if (!exists) {
                        return [...prev, { tunnelId: allTunnelIds[index], data: query.data }];
                    }
                    return prev;
                });
            }
        });
    }, [tunnelActionsQueries, allTunnelIds]);

    const extractTunnelData = useMemo(() => {
        const activeTunnelNames = new Set(tunnelsByStatus.active.map(tunnel => tunnel.name));
        const filteredDefaultActiveTunnels = tunnelsByStatus.defaultActive.filter(
            tunnel => !activeTunnelNames.has(tunnel.name)
        );

        return {
            activeTunnels: processTunnels(tunnelsByStatus.active, tunnelActionResults),
            defaultActiveTunnels: processTunnels(filteredDefaultActiveTunnels, tunnelActionResults),
        };
    }, [tunnelsByStatus.active, tunnelsByStatus.defaultActive, tunnelActionResults]);

    return (
        <Loader loading={tunnelLoader || tunnelActionsLoading}>
            <Box sx={rootSx}>
                <ActiveTunnelTabs tunnelData={extractTunnelData} />
            </Box>
            <Box sx={buttonContainerSx}>
                <CancelButton onClick={onCancel}>{texts.RETOUR}</CancelButton>
            </Box>
        </Loader>
    );
};

TunnelActiveVersion.propTypes = {
    onCancel: PropTypes.func.isRequired,
};

export default TunnelActiveVersion;
